import * as THREE from 'three';

interface GridUniforms extends Record<string, THREE.IUniform> {
    time: THREE.IUniform<number>;
    baseColor: THREE.IUniform<THREE.Color>;
    reflectionColor: THREE.IUniform<THREE.Color>;
    fogColor: THREE.IUniform<THREE.Color>;
    fogDensity: THREE.IUniform<number>;
    cameraPos: THREE.IUniform<THREE.Vector3>;
    rippleSpeed: THREE.IUniform<number>;
    rippleIntensity: THREE.IUniform<number>;
    glowIntensity: THREE.IUniform<number>;
    opacity: THREE.IUniform<number>;
}

interface UniformRanges {
    [key: string]: [number, number];
    rippleSpeed: [number, number];
    rippleIntensity: [number, number];
    glowIntensity: [number, number];
    opacity: [number, number];
}

export class Grid {
    private water: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial> | null = null;

    constructor() {
        this.createWater();
    }

    private createWater(): THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial> {
        console.log('Initializing water surface');
        const geometry = new THREE.PlaneGeometry(2000, 2000, 100, 100); // Reduced vertex count for better performance
        
        // Custom shader material for the water surface
        const material = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                baseColor: { value: new THREE.Color(0x000000) },
                reflectionColor: { value: new THREE.Color(0xff0000) },
                fogColor: { value: new THREE.Color(0x000000) },
                fogDensity: { value: 0.01 },
                cameraPos: { value: new THREE.Vector3(0, 25, 50) },
                rippleSpeed: { value: 8.0 },
                rippleIntensity: { value: 0.7 },
                glowIntensity: { value: 2.0 },
                opacity: { value: 0.9 }
            } as GridUniforms,
            vertexShader: `
                varying vec2 vUv;
                varying float vFogDepth;
                varying float vElevation;
                uniform float time;
                uniform vec3 cameraPos;
                
                // Simplex noise function
                vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
                vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
                vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }
                
                float snoise(vec2 v) {
                    const vec4 C = vec4(0.211324865405187,
                                      0.366025403784439,
                                     -0.577350269189626,
                                      0.024390243902439);
                    vec2 i  = floor(v + dot(v, C.yy) );
                    vec2 x0 = v -   i + dot(i, C.xx);
                    vec2 i1;
                    i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
                    vec4 x12 = x0.xyxy + C.xxzz;
                    x12.xy -= i1;
                    i = mod289(i);
                    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
                        + i.x + vec3(0.0, i1.x, 1.0 ));
                    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
                    m = m*m ;
                    m = m*m ;
                    vec3 x = 2.0 * fract(p * C.www) - 1.0;
                    vec3 h = abs(x) - 0.5;
                    vec3 ox = floor(x + 0.5);
                    vec3 a0 = x - ox;
                    m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
                    vec3 g;
                    g.x  = a0.x  * x0.x  + h.x  * x0.y;
                    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
                    return 130.0 * dot(m, g);
                }
                
                void main() {
                    vUv = uv;
                    float slowTime = time * 0.0002;
                    
                    // Calculate distance to camera for wave modulation
                    vec2 toCamera = vec2(position.x - cameraPos.x, position.z - cameraPos.z);
                    float distToCamera = length(toCamera) * 0.01;
                    
                    // Create base waves
                    float baseWave = snoise(vec2(uv.x * 3.0 + slowTime, uv.y * 3.0)) * 0.5;
                    
                    // Add detailed waves that intensify near camera
                    float viewInfluence = smoothstep(1.5, 0.0, distToCamera);
                    float detailWave = snoise(vec2(uv.x * 15.0 + slowTime * 2.0, uv.y * 15.0)) * 0.2;
                    detailWave *= viewInfluence;
                    
                    // Add radial waves from camera position
                    float radialWave = sin(distToCamera * 10.0 - slowTime * 3.0) * 0.15;
                    radialWave *= viewInfluence;
                    
                    vElevation = baseWave + detailWave + radialWave;
                    
                    vec3 newPosition = position + normal * vElevation * 2.0;
                    vec4 mvPosition = modelViewMatrix * vec4(newPosition, 1.0);
                    gl_Position = projectionMatrix * mvPosition;
                    vFogDepth = -mvPosition.z;
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform vec3 baseColor;
                uniform vec3 reflectionColor;
                uniform vec3 cameraPos;
                uniform vec3 fogColor;
                uniform float fogDensity;
                uniform float rippleSpeed;
                uniform float rippleIntensity;
                uniform float glowIntensity;
                uniform float opacity;
                
                varying vec2 vUv;
                varying float vFogDepth;
                varying float vElevation;
                
                void main() {
                    float slowTime = time * 0.0002;
                    
                    // Calculate view-based ripples
                    vec2 worldPos = vec2(vUv.x * 2000.0 - 1000.0, vUv.y * 2000.0 - 1000.0);
                    vec2 toCamera = vec2(cameraPos.x - worldPos.x, cameraPos.z - worldPos.y);
                    float distToCamera = length(toCamera) * 0.015;
                    
                    // Create view-dependent ripples
                    float viewInfluence = smoothstep(1.2, 0.0, distToCamera);
                    float rippleFreq = mix(8.0, 16.0, viewInfluence);
                    float ripple = sin(distToCamera * rippleFreq - slowTime * rippleSpeed) * 0.5 + 0.5;
                    ripple *= smoothstep(1.0, 0.0, distToCamera) * rippleIntensity;
                    
                    // Base water color with height variation
                    float heightFactor = smoothstep(-0.3, 0.3, vElevation);
                    vec3 waterColor = mix(baseColor, reflectionColor * 0.3, heightFactor * 0.6);
                    
                    // Add intense ripple and glow near camera
                    float glow = smoothstep(0.8, 0.0, distToCamera) * glowIntensity;
                    waterColor = mix(waterColor, reflectionColor * 0.8, ripple);
                    waterColor = mix(waterColor, reflectionColor * 2.0, glow * 0.5);
                    
                    // Add dynamic pulse that follows view
                    float viewPulse = sin(slowTime * 12.0 + distToCamera * 4.0) * 0.5 + 0.5;
                    waterColor += reflectionColor * glow * viewPulse * 0.5;
                    
                    // Apply distance-based fog
                    float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
                    vec3 finalColor = mix(waterColor, fogColor, fogFactor);
                    
                    gl_FragColor = vec4(finalColor, mix(opacity, 1.0, glow * 0.3));
                }
            `,
            transparent: true,
            depthWrite: false,
            side: THREE.DoubleSide
        });

        this.water = new THREE.Mesh(geometry, material);
        this.water.rotation.x = -Math.PI / 2;
        this.water.position.y = -0.5;
        
        return this.water;
    }

    updateUniforms(name: keyof GridUniforms, value: number): void {
        if (!this.water?.material?.uniforms?.[name]) return;
        
        // Validate ranges
        const ranges: UniformRanges = {
            rippleSpeed: [0, 20],
            rippleIntensity: [0, 2],
            glowIntensity: [0, 5],
            opacity: [0, 1]
        };
        
        if (ranges[name]) {
            const [min, max] = ranges[name];
            value = Math.max(min, Math.min(max, value));
        }
        
        (this.water.material.uniforms[name] as { value: number }).value = value;
    }

    update(time: number): void {
        if (this.water?.material?.uniforms) {
            this.water.material.uniforms.time.value = time;
        }
    }

    dispose(): void {
        // Cleanup geometries
        this.water?.geometry?.dispose();
        // Cleanup materials
        this.water?.material?.dispose();
        // Remove from scene
        this.water?.parent?.remove(this.water);
        // Cleanup textures
        if (this.water?.material?.uniforms) {
            Object.values(this.water.material.uniforms)
                .forEach(uniform => {
                    if (uniform.value instanceof THREE.Texture) {
                        uniform.value.dispose();
                    }
                });
        }
    }

    updateCameraPosition(camera: THREE.Camera): void {
        if (this.water?.material?.uniforms) {
            this.water.material.uniforms.cameraPos.value.copy(camera.position);
        }
    }

    addToScene(scene: THREE.Scene): void {
        if (this.water) {
            scene.add(this.water);
        }
    }
}
